import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";

import { getProdOrNonProd } from "@swa-ui/dotcom";
import { isResponseOk, useFetch } from "@swa-ui/fetch";
import { WifiAuthenticationContext } from "@swa-ui/inflight";
import { isEmpty } from "@swa-ui/object";
import { usePersistedState } from "@swa-ui/persistence";

import {
  WIFI_PROVIDER_ENDPOINT,
  WIFI_USERS_UNSANDBOX_DEVICE_ENDPOINT,
} from "../defines/apiEndpoints";
import { getMockRouterDetailsForNonRIG } from "../defines/getMockRouterDetailsForNonRIG";

export const InflightUserStatusContext = createContext();
export const InflightUserStatusProvider = (props) => {
  const { children } = props;
  const { wifiAuthenticationState } = useContext(WifiAuthenticationContext);
  const { post, response } = useFetch();
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const unsandboxInFlight = useRef(false);
  const [sandboxStatus, setSandboxStatus] = usePersistedState({
    defaultValue: {
      clientId: "",
      routerId: "",
      sandboxed: true,
      sandboxId: null,
    },
    key: "client-sandbox-status",
    timeToLiveInMinutes: 30,
  });

  useEffect(() => {
    getUserSandboxData();
  }, []);

  useEffect(() => {
    if (!unsandboxInFlight.current && shouldUnSandboxDevice()) {
      unsandboxInFlight.current = true;
      updateSandboxStatus();
    }
  }, [sandboxStatus]);

  const contextValue = useMemo(
    () => ({
      error,
      loading,
      sandboxStatus,
      setSandboxStatus,
    }),
    [error, loading, sandboxStatus, setSandboxStatus]
  );

  return (
    <InflightUserStatusContext.Provider value={contextValue}>
      {children}
    </InflightUserStatusContext.Provider>
  );

  async function getUserSandboxData() {
    setLoading(true);

    try {
      const res = await fetch(WIFI_PROVIDER_ENDPOINT);

      if (res.ok) {
        const body = await res.json();

        setSandboxStatus({
          ...sandboxStatus,
          ...body,
        });
      } else {
        handleGetSandboxError();
      }
    } catch {
      handleGetSandboxError();
    }

    setLoading(false);
  }

  // The getUserSandboxData() will return an error in DEV/QA environment
  // as the url is not present in these environments.
  // We are mocking the router details to test the flow
  // as the router API is available only in RIG environment.

  function handleGetSandboxError() {
    const { clientId, routerId } = sandboxStatus;
    const isNonProdEnv = getProdOrNonProd() !== "prod";

    if (isNonProdEnv && isEmpty(clientId) && isEmpty(routerId)) {
      setSandboxStatus({
        ...getMockRouterDetailsForNonRIG(),
      });
    }
  }

  function shouldUnSandboxDevice() {
    const { clientId, sandboxed } = sandboxStatus;

    return clientId && sandboxed && wifiAuthenticationState === "fullAuth";
  }

  async function updateSandboxStatus() {
    const { clientId, routerId, sandboxId } = sandboxStatus;

    setLoading(true);

    const body = await post(WIFI_USERS_UNSANDBOX_DEVICE_ENDPOINT, {
      client_id: clientId,
      router_id: routerId,
      sandbox_id: sandboxId,
    });

    if (isResponseOk(response)) {
      setSandboxStatus({
        ...sandboxStatus,
        ...body,
        sandboxed: false,
      });

      setLoading(false);
    } else {
      unsandboxInFlight.current = false;
      setError(true);
      setLoading(false);
    }
  }
};
